// Tool definitions per Olli (Claude API tool_use)
// I nomi dei campi sono allineati al data model reale in useAppState.js

export const OLLI_TOOLS = [
  {
    name: 'create_invoice',
    description:
      'Crea una nuova fattura. Usa questa azione quando l\'utente chiede di creare, emettere o registrare una fattura.',
    input_schema: {
      type: 'object',
      properties: {
        clientName: {
          type: 'string',
          description: 'Nome del cliente',
        },
        grossAmount: {
          type: 'number',
          description: 'Importo lordo in EUR',
        },
        issueDate: {
          type: 'string',
          description: 'Data emissione in formato YYYY-MM-DD. Default: oggi.',
        },
        dueDate: {
          type: 'string',
          description:
            'Data scadenza in formato YYYY-MM-DD. Default: issueDate + 15 giorni.',
        },
        notes: {
          type: 'string',
          description: 'Note opzionali sulla fattura',
        },
      },
      required: ['clientName', 'grossAmount'],
    },
  },
  {
    name: 'mark_invoice_paid',
    description:
      'Segna una o più fatture come incassate. Cerca per numero fattura o nome cliente.',
    input_schema: {
      type: 'object',
      properties: {
        identifier: {
          type: 'string',
          description:
            'Numero fattura (invoiceCode) o nome cliente per identificare la fattura.',
        },
        paymentDate: {
          type: 'string',
          description:
            'Data di incasso in formato YYYY-MM-DD. Default: oggi.',
        },
      },
      required: ['identifier'],
    },
  },
  {
    name: 'search_invoices',
    description:
      'Cerca fatture con filtri opzionali. year/month = data di EMISSIONE (fatturato); paymentYear/paymentMonth = data di PAGAMENTO (incassato, principio di cassa). Restituisce le fatture trovate e il campo `totale` (somma dei grossAmount).',
    input_schema: {
      type: 'object',
      properties: {
        clientName: {
          type: 'string',
          description: 'Filtra per nome cliente (ricerca parziale)',
        },
        paid: {
          type: 'boolean',
          description: 'Filtra per stato pagamento: true = incassate, false = da incassare',
        },
        year: {
          type: 'number',
          description: 'Filtra per anno di EMISSIONE (issueDate) — usalo per il fatturato',
        },
        month: {
          type: 'number',
          description: 'Filtra per mese di EMISSIONE (1-12)',
        },
        paymentYear: {
          type: 'number',
          description: 'Anno di PAGAMENTO (paymentDate) — usalo per filtrare per cassa (incassato)',
        },
        paymentMonth: {
          type: 'number',
          description: 'Mese di PAGAMENTO (1-12) — per filtrare per cassa (incassato)',
        },
      },
    },
  },
  {
    name: 'update_invoice',
    description:
      'Modifica una fattura esistente. Identificata per numero fattura o ID.',
    input_schema: {
      type: 'object',
      properties: {
        identifier: {
          type: 'string',
          description: 'Numero fattura (invoiceCode) o ID della fattura da modificare',
        },
        updates: {
          type: 'object',
          description: 'Campi da aggiornare',
          properties: {
            clientName: { type: 'string' },
            grossAmount: { type: 'number' },
            issueDate: { type: 'string' },
            dueDate: { type: 'string' },
            notes: { type: 'string' },
            paid: { type: 'boolean' },
            paymentDate: { type: 'string' },
          },
        },
      },
      required: ['identifier', 'updates'],
    },
  },
  {
    name: 'delete_invoices',
    description:
      'Cancella una o più fatture. RICHIEDE SEMPRE conferma dell\'utente prima di procedere.',
    input_schema: {
      type: 'object',
      properties: {
        invoiceIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Lista di ID fatture da eliminare',
        },
        confirmed: {
          type: 'boolean',
          description:
            'Se false, chiedi conferma all\'utente. Se true, procedi con la cancellazione.',
        },
      },
      required: ['invoiceIds', 'confirmed'],
    },
  },
  {
    name: 'export_invoices_csv',
    description:
      'Esporta le fatture in formato CSV. Genera un file scaricabile.',
    input_schema: {
      type: 'object',
      properties: {
        year: {
          type: 'number',
          description: 'Anno delle fatture da esportare. Default: anno selezionato.',
        },
        paid: {
          type: 'boolean',
          description: 'Filtra solo incassate (true) o non incassate (false). Ometti per tutte.',
        },
      },
    },
  },
  {
    name: 'create_client',
    description: 'Crea un nuovo cliente nell\'anagrafica.',
    input_schema: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Nome o ragione sociale del cliente',
        },
        email: {
          type: 'string',
          description: 'Email del cliente',
        },
        pecEmail: {
          type: 'string',
          description: 'PEC del cliente',
        },
        fiscalCode: {
          type: 'string',
          description: 'Codice fiscale',
        },
        vatNumber: {
          type: 'string',
          description: 'Partita IVA del cliente',
        },
        sdiCode: {
          type: 'string',
          description: 'Codice SDI per fatturazione elettronica',
        },
        phone: {
          type: 'string',
          description: 'Numero di telefono',
        },
        address: {
          type: 'string',
          description: 'Indirizzo',
        },
        compensationType: {
          type: 'string',
          enum: ['daily', 'hourly', 'fixed'],
          description: 'Tipo compenso: daily (giornaliero), hourly (orario), fixed (fisso)',
        },
        rate: {
          type: 'number',
          description: 'Tariffa (giornaliera, oraria o fissa a seconda di compensationType)',
        },
        paymentTermsDays: {
          type: 'number',
          description: 'Giorni per il pagamento. Default: 30.',
        },
        notes: {
          type: 'string',
          description: 'Note opzionali',
        },
      },
      required: ['name'],
    },
  },
  {
    name: 'log_timesheet_entry',
    description:
      'Registra una presenza nel timesheet di un cliente per un giorno specifico.',
    input_schema: {
      type: 'object',
      properties: {
        clientId: {
          type: 'string',
          description: 'ID del cliente',
        },
        clientName: {
          type: 'string',
          description: 'Nome del cliente (usato se clientId non disponibile)',
        },
        year: {
          type: 'number',
          description: 'Anno del timesheet',
        },
        month: {
          type: 'number',
          description: 'Mese del timesheet (1-12)',
        },
        day: {
          type: 'number',
          description: 'Giorno del mese (1-31)',
        },
        value: {
          type: 'number',
          enum: [0, 0.5, 1],
          description: 'Valore presenza: 0 = assente, 0.5 = mezza giornata, 1 = giornata intera',
        },
        task: {
          type: 'string',
          description: 'Descrizione attività svolta',
        },
      },
      required: ['year', 'month', 'day', 'value'],
    },
  },
  {
    name: 'generate_timesheet_invoice',
    description:
      'Genera una fattura dal timesheet di un mese specifico per un cliente.',
    input_schema: {
      type: 'object',
      properties: {
        clientId: {
          type: 'string',
          description: 'ID del cliente',
        },
        clientName: {
          type: 'string',
          description: 'Nome del cliente (alternativa a clientId)',
        },
        year: {
          type: 'number',
          description: 'Anno del timesheet',
        },
        month: {
          type: 'number',
          description: 'Mese del timesheet (1-12)',
        },
      },
      required: ['year', 'month'],
    },
  },
  {
    name: 'send_payment_reminder',
    description:
      'Invia un sollecito di pagamento via email per una fattura non incassata.',
    input_schema: {
      type: 'object',
      properties: {
        invoiceIdentifier: {
          type: 'string',
          description: 'Numero fattura o nome cliente per identificare la fattura da sollecitare',
        },
      },
      required: ['invoiceIdentifier'],
    },
  },
  {
    name: 'start_csv_import',
    description:
      'Apre la modale di importazione CSV per importare fatture da file.',
    input_schema: {
      type: 'object',
      properties: {},
    },
  },
];
